import React from "react";
import { format } from "date-fns";
import _ from "lodash";
import { motion, AnimatePresence } from "framer-motion";
import { User, Calendar, FileText, Info, Briefcase } from "lucide-react";

export default function SummaryList({ message }) {
  return (
    <div className="w-full">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-hidden bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-blue-gray-900 shadow-sm">
        <table className="w-full text-sm text-left rtl:text-right">
          <thead className="text-xs text-gray-500 uppercase bg-gray-50/50 dark:bg-gray-800/50 dark:text-gray-400 border-b border-gray-100 dark:border-blue-gray-900 font-bold">
            <tr>
              <th scope="col" className="px-6 py-4 tracking-wider">
                Team Member
              </th>
              <th scope="col" className="px-6 py-4 tracking-wider whitespace-nowrap w-42">
                Date
              </th>
              <th scope="col" className="px-6 py-4 tracking-wider">
                Activity Summary
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {(!_.isEmpty(message) && message?.TotalCount > 0) ? (
              <AnimatePresence mode="popLayout">
                {message?.SummaryList?.map((msg, index) => (
                  <motion.tr
                    key={msg.Id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="group bg-transparent hover:bg-blue-50/20 dark:hover:bg-blue-900/10 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <img
                          className="w-9 h-9 rounded-lg object-cover border border-gray-100 dark:border-blue-gray-900"
                          src={msg.Photopath}
                          alt={msg.MemberName}
                        />
                        <div className="ps-3">
                          <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
                            {msg.MemberName}
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                            {msg.Designation}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[13px] font-bold text-gray-700 dark:text-gray-300">
                        {format(new Date(msg.SummaryDate), "dd MMM yyyy")}
                      </div>
                      <div className="text-[10px] text-gray-400 dark:text-gray-500 uppercase font-bold tracking-tighter">
                        {format(new Date(msg.SummaryDate), "EEEE")}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
                        {msg.Summary}
                      </p>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            ) : (
              <tr>
                <td colSpan="3" className="px-6 py-20 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                      <Info className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 font-medium text-lg">No activity summaries found.</p>
                    <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Try adjusting your filters or date range.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {(!_.isEmpty(message) && message?.TotalCount > 0) ? (
          message?.SummaryList?.map((msg, index) => (
            <motion.div
              key={msg.Id || index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white dark:bg-gray-900/50 backdrop-blur-md rounded-2xl p-5 border border-gray-100 dark:border-blue-gray-900 shadow-sm active:scale-95 transition-transform"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img
                    className="w-12 h-12 rounded-xl object-cover shadow-sm"
                    src={msg.Photopath}
                    alt={msg.MemberName}
                  />
                  <div>
                    <div className="text-base font-bold text-gray-900 dark:text-white">
                      {msg.MemberName}
                    </div>
                    <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      {msg.Designation}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-tighter">
                    {format(new Date(msg.SummaryDate), "dd MMM")}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {format(new Date(msg.SummaryDate), "yyyy")}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-black/40 p-3 rounded-xl">
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {msg.Summary}
                </p>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-20 bg-gray-50 dark:bg-gray-900/20 rounded-3xl border-2 border-dashed border-gray-200 dark:border-blue-gray-900">
            <Info className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 font-medium">No results found.</p>
          </div>
        )}
      </div>
    </div>
  );
}
